// Storm fronts (roadmap #12's look, TODO "moving weather fronts"): on storm
// rounds a line of anvil-topped thunderheads marches across the map, lightning
// flickering in their bellies, bolts cracking down to the ground, rain
// curtains dragging beneath. Theater-agnostic by construction — the only
// gameplay input is the weather byte every theater's snapshot already
// carries, so air, naval, land, and any future mode get the same sky.
//
// Determinism is the whale-schedule trick from ambient.js: every position,
// flicker, and strike is a pure function of (world seed, server time), so all
// clients watch the same storm with zero wire traffic. The front's path wraps
// forever — crossing k of the map ends where crossing k+1 begins with a fresh
// hashed bearing — which means a client joining mid-round lands on exactly
// the same sky as everyone else, no round-start state needed.
//
// Purely audiovisual: nothing here pushes on planes, hulls, spotting, or
// scores. A client with storms hidden runs an identical simulation.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { makeGlowTexture } from "./world.js";

const LIGHTNING_CORE = 0xeaf2ff; // the bolt itself: cold blue-white
const GLOW_TINT = 0xcfe0ff; // in-cloud flicker and ground flash

export class StormFront {
  constructor(scene, field, config) {
    const cfg = config.storm ?? {};
    this.scene = scene;
    this.field = field;
    // Bigger maps (naval fights at 3x the air map's radius) get bigger, faster
    // cells so a front still reads as weather, not a lost puff — and crosses
    // in about the same wall-clock time.
    const mapK = Math.max(1, Math.min(2.6, field.radius / 3600));
    this.cellN = cfg.cellCount ?? 4;
    this.cellR = (cfg.cellRadiusM ?? 850) * mapK;
    this.baseY = cfg.cloudBaseM ?? 380;
    this.topY = cfg.cloudTopM ?? 2100;
    this.speed = (cfg.marchSpeedMS ?? 12) * mapK;
    this.flickerEvery = cfg.flickerEverySec ?? 5;
    this.strikeEvery = cfg.strikeEverySec ?? 16;
    this.seed = (field.seed + 62199) >>> 0;
    // Path length: the front walks fully onto the map and fully off the far
    // side before the next crossing re-rolls its bearing.
    this.pathLen = 2 * (field.radius + this.cellR * 2.2);

    this.active = false;
    this.built = false;
    this.crossK = -1;
    this.cellOff = []; // per-cell local [x,z] within the front group
    this.cellWorld = []; // per-cell world-space centers, refreshed per frame
    this.strikes = []; // per-cell live strike state (world-anchored)
    this.onStrike = null; // (Vector3 world pos) => {} — main.js hangs thunder here
    this.insideK = 0; // 0..1 "camera is in the weather" (debug/test introspection)
  }

  // Lazy build: four rounds in five never storm, so the ~1500 instances only
  // exist once a storm round actually arrives.
  build() {
    this.built = true;
    const N = this.cellN;
    const r = this.cellR;
    const seed = this.seed;
    this.group = new THREE.Group();
    this.group.visible = false;
    this.scene.add(this.group);

    // --- the cloud mass: one instanced mesh for the whole front ------------
    // Same recipe as buildClouds (translucent Lambert boxes, depthWrite off
    // so puffs melt together), but the blocks run SMALL and DENSE — a wall of
    // heavily-overlapped translucent slivers reads as weather, where a few
    // big slabs read as masonry. Two populations share the mesh: per-cell
    // convective towers (the lightning anchors), and a continuous squall
    // line — a ragged dark base wall plus one connected anvil sheet spanning
    // the whole front, so the cells merge into a single storm system instead
    // of four lonely block-castles.
    const PER_CELL = 300;
    this.spacing = r * 1.7; // cells overlap through the line mass
    const halfLine = ((N - 1) / 2) * this.spacing + r * 0.7;
    const anvilY = this.baseY + 0.62 * (this.topY - this.baseY);
    const col = new THREE.Color();
    const cLow = new THREE.Color(0.12, 0.14, 0.18); // rain-black belly
    const cHigh = new THREE.Color(0.52, 0.56, 0.65); // daylit mid-tower
    const cAnvil = new THREE.Color(0.8, 0.84, 0.9); // sun catches the top
    const shade = (y, b, cs) => {
      const hFrac = (y - this.baseY) / (this.topY - this.baseY);
      col.copy(cLow).lerp(cHigh, Math.min(1, Math.max(0, hFrac) * 1.05));
      if (y > anvilY) col.lerp(cAnvil, (y - anvilY) / (this.topY - anvilY));
      const jit = 0.94 + 0.12 * hash2(b, 6, cs);
      return [col.r * jit, col.g * jit, col.b * jit];
    };
    // The blocks run SMALL and DENSE — a wall of heavily-overlapped
    // translucent slivers reads as weather where a few big slabs read as
    // masonry, and (tried, rejected) paper-thin mega-sheets read as smog.
    // Chunky-but-small is the storm that looked like a storm.
    this.towerLocal = []; // per-cell block layout, cell-relative (built once)
    for (let i = 0; i < N; i++) {
      const blocks = [];
      const cs = (seed + i * 131) >>> 0;
      for (let b = 0; b < PER_CELL; b++) {
        let x, y, z, sx, sy, sz;
        const h1 = hash2(b, 1, cs), h2 = hash2(b, 2, cs), h3 = hash2(b, 3, cs);
        const h4 = hash2(b, 4, cs), h5 = hash2(b, 5, cs);
        if (b < 90) {
          // Skirt: wide, low, the darkest blocks — the wall you fly under.
          const ang = h1 * Math.PI * 2, rad = r * (0.15 + 0.85 * Math.sqrt(h2));
          const s = r * (0.09 + 0.09 * h4);
          x = Math.cos(ang) * rad; z = Math.sin(ang) * rad;
          y = this.baseY + h3 * 170;
          sx = s * 1.7; sy = s * 0.55; sz = s * 1.4;
        } else if (b < 210) {
          // Tower: the convective column, narrowing as it climbs.
          const t = h3;
          const ang = h1 * Math.PI * 2;
          const rad = r * (0.85 - 0.5 * t) * Math.sqrt(h2);
          const s = r * (0.075 + 0.08 * h4);
          x = Math.cos(ang) * rad; z = Math.sin(ang) * rad;
          y = this.baseY + 130 + t * (anvilY - this.baseY - 130);
          sx = s * 1.2; sy = s * (0.7 + 0.7 * h5); sz = s * 1.1;
        } else {
          // Anvil: flat plates spreading at the cap and streaming forward
          // (+Z is the march direction — the shear the shape is named for).
          const t = h3; // 0 anvil base -> 1 cap
          const spread = r * (0.8 + 1.1 * t);
          const ang = h1 * Math.PI * 2, rad = spread * Math.sqrt(h2);
          const s = r * (0.11 + 0.11 * h4);
          x = Math.cos(ang) * rad;
          z = Math.sin(ang) * rad + t * r * 0.7 * h5;
          y = anvilY + t * (this.topY - anvilY);
          sx = s * 2.3; sy = s * 0.18; sz = s * 1.7;
        }
        blocks.push([x, y, z, sx, sy, sz, ...shade(y, b, cs)]);
      }
      this.towerLocal.push(blocks);
    }
    // The squall line: group-local, crossing-independent (only the cells
    // re-jitter per crossing), so it bakes once here and re-lays verbatim.
    this.lineLocal = [];
    {
      const cs = (seed + 7331) >>> 0;
      const LINE_LOW = 500, LINE_ANVIL = 320;
      for (let b = 0; b < LINE_LOW; b++) {
        const h1 = hash2(b, 11, cs), h2 = hash2(b, 12, cs), h3 = hash2(b, 13, cs);
        const h4 = hash2(b, 14, cs), h5 = hash2(b, 15, cs);
        const x = (h1 - 0.5) * 2 * halfLine;
        const z = (h2 - 0.5) * r * 1.5;
        const y = this.baseY + Math.pow(h3, 1.6) * 480; // bias into the dark base
        const s = r * (0.08 + 0.1 * h4);
        this.lineLocal.push([x, y, z, s * 1.9, s * (0.5 + 0.4 * h5), s * 1.5, ...shade(y, b, cs)]);
      }
      for (let b = 0; b < LINE_ANVIL; b++) {
        const h1 = hash2(b, 21, cs), h2 = hash2(b, 22, cs), h3 = hash2(b, 23, cs);
        const h4 = hash2(b, 24, cs), h5 = hash2(b, 25, cs);
        const t = h3;
        const x = (h1 - 0.5) * 2 * (halfLine + r * 0.3);
        const z = (h2 - 0.5) * r * 1.8 + t * r * 0.6 * h5;
        const y = anvilY + 80 + t * (this.topY - anvilY - 80);
        const s = r * (0.12 + 0.12 * h4);
        this.lineLocal.push([x, y, z, s * 2.6, s * 0.16, s * 1.9, ...shade(y, 100 + b, cs)]);
      }
    }
    this.towers = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0xffffff, transparent: true, opacity: 0.84, depthWrite: false,
      }),
      N * PER_CELL + this.lineLocal.length,
    );
    this.towers.frustumCulled = false;
    this.group.add(this.towers);

    // --- rain shafts: a faint gray veil from cloud base to the ground,
    // under the cells AND the line between them — one curtain, not four.
    this.veil = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0x7d8894, transparent: true, opacity: 0.12, depthWrite: false,
      }),
      N * 7 + (N - 1) * 4,
    );
    this.veil.frustumCulled = false;
    this.group.add(this.veil);

    // --- rain streaks: falling instanced slivers under each cell -----------
    this.RAIN_PER_CELL = 120;
    this.rain = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0xa9c0d4, transparent: true, opacity: 0.4, depthWrite: false,
      }),
      N * this.RAIN_PER_CELL,
    );
    this.rain.frustumCulled = false;
    this.group.add(this.rain);

    // --- close rain: a small camera-following curtain so being under the
    // cell feels like rain on the canopy, not just scenery in the distance.
    this.CLOSE_RAIN = 200;
    this.closeRain = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.14, 9, 0.14),
      new THREE.MeshBasicMaterial({
        color: 0xbdd2e2, transparent: true, opacity: 0, depthWrite: false,
      }),
      this.CLOSE_RAIN,
    );
    this.closeRain.frustumCulled = false;
    this.scene.add(this.closeRain); // camera-space, not part of the front

    // --- lightning: an in-cloud glow sprite per cell, a ground flash, a
    // shared pool of bolt segments — and the glow that actually matters:
    // per-instance CLOUD COLORS lerp toward the lightning tint around a
    // live stroke (see update), so the cloud itself glows. A real
    // PointLight can't do this job here: three r160's physical
    // inverse-square falloff is stone dead across cloud-sized distances.
    const glowTex = makeGlowTexture();
    this.glows = [];
    this.groundGlows = [];
    for (let i = 0; i < N; i++) {
      const g = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: GLOW_TINT, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending,
      }));
      g.scale.set(this.cellR * 1.7, this.cellR * 1.2, 1);
      this.group.add(g);
      this.glows.push(g);
      const gg = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: GLOW_TINT, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending,
      }));
      gg.scale.set(320, 220, 1);
      this.scene.add(gg); // world-anchored with its bolt
      this.groundGlows.push(gg);
    }
    // Per-instance illumination bookkeeping: base color, owning cell, and
    // distance falloff to that cell's glow point (filled by layoutCells).
    const cap = N * PER_CELL + this.lineLocal.length;
    this.instBase = new Float32Array(cap * 3);
    this.instCell = new Uint8Array(cap);
    this.instK = new Float32Array(cap);
    this.instCount = cap;
    this._wasLit = false;
    // Segment budget: a stair-stepped ground bolt runs ~25 boxes plus a
    // fork; a cloud-to-cloud arc ~17. Park the rest at zero scale.
    this.C2G_SEGS = 34;
    this.C2C_SEGS = 18;
    this.bolt = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: LIGHTNING_CORE, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending, fog: false,
      }),
      N * this.C2G_SEGS + (N - 1) * this.C2C_SEGS,
    );
    this.bolt.frustumCulled = false;
    this.scene.add(this.bolt);

    this.dummy = new THREE.Object3D();
    this.strikes = new Array(N).fill(null);
    this.lastStrikeSlot = new Array(N).fill(-1);
    this.arcs = new Array(Math.max(0, N - 1)).fill(null);
    this.lastArcSlot = new Array(Math.max(0, N - 1)).fill(-1);
    this.cellGlow = new Array(N).fill(0);
  }

  // Re-deal the front line for crossing k: cells sit abreast of the march
  // (local X), staggered and jittered so no two crossings look alike.
  layoutCells(k) {
    this.crossK = k;
    const N = this.cellN;
    this.cellOff.length = 0;
    for (let i = 0; i < N; i++) {
      const ox = (i - (N - 1) / 2) * this.spacing + (hash2(k, 21 + i, this.seed) - 0.5) * this.cellR * 0.7;
      const oz = (hash2(k, 41 + i, this.seed) - 0.5) * this.cellR * 1.6;
      this.cellOff.push([ox, oz]);
    }
    // Glow anchors first (the flicker/strike hearts) — every block's
    // illumination falloff is measured against its cell's anchor.
    const gp = [];
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      gp.push([
        ox + (hash2(k, 81 + i, this.seed) - 0.5) * this.cellR * 0.6,
        this.baseY + (this.topY - this.baseY) * 0.32,
        oz + (hash2(k, 91 + i, this.seed) - 0.5) * this.cellR * 0.6,
      ]);
    }
    // Bake the cloud-mass matrices for this deal: cells first, then the
    // crossing-independent squall line. Alongside each matrix, remember the
    // block's base color, owning cell, and glow falloff so lightning can
    // relight the mass per frame without recomputing any of this.
    let n = 0;
    const put = (px, py, pz, sx, sy, sz, cr, cg, cb, ci) => {
      this.dummy.position.set(px, py, pz);
      this.dummy.scale.set(sx, sy, sz);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.towers.setMatrixAt(n, this.dummy.matrix);
      this.towers.setColorAt(n, _c.setRGB(cr, cg, cb));
      if (ci === undefined) {
        // Line blocks belong to whichever cell's glow sits closest.
        let bd = Infinity;
        ci = 0;
        for (let i = 0; i < N; i++) {
          const d = Math.hypot(px - gp[i][0], py - gp[i][1], pz - gp[i][2]);
          if (d < bd) { bd = d; ci = i; }
        }
      }
      const d = Math.hypot(px - gp[ci][0], py - gp[ci][1], pz - gp[ci][2]);
      this.instBase[n * 3] = cr;
      this.instBase[n * 3 + 1] = cg;
      this.instBase[n * 3 + 2] = cb;
      this.instCell[n] = ci;
      this.instK[n] = Math.exp(-d / (this.cellR * 0.95));
      n++;
    };
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      for (const [x, y, z, sx, sy, sz, cr, cg, cb] of this.towerLocal[i]) {
        put(ox + x, y, oz + z, sx, sy, sz, cr, cg, cb, i);
      }
    }
    for (const [x, y, z, sx, sy, sz, cr, cg, cb] of this.lineLocal) {
      put(x, y, z, sx, sy, sz, cr, cg, cb, undefined);
    }
    this.towers.instanceMatrix.needsUpdate = true;
    if (this.towers.instanceColor) this.towers.instanceColor.needsUpdate = true;
    let v = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      const cs = (this.seed + k * 977 + i * 131) >>> 0;
      for (let b = 0; b < 7; b++) {
        const ang = hash2(b, 61, cs) * Math.PI * 2;
        const rad = this.cellR * 0.55 * Math.sqrt(hash2(b, 62, cs));
        const w = this.cellR * (0.35 + 0.3 * hash2(b, 63, cs));
        this.dummy.position.set(ox + Math.cos(ang) * rad, this.baseY / 2, oz + Math.sin(ang) * rad);
        this.dummy.scale.set(w, this.baseY, w * 0.8);
        this.dummy.rotation.set(0, ang, 0);
        this.dummy.updateMatrix();
        this.veil.setMatrixAt(v++, this.dummy.matrix);
      }
      // ...and a rain curtain in the gap toward the next cell over.
      if (i + 1 < N) {
        const [nx, nz] = [
          (i + 1 - (N - 1) / 2) * this.spacing, 0,
        ];
        for (let b = 0; b < 4; b++) {
          const fx = ox + (nx - ox) * (0.2 + 0.6 * hash2(b, 64, cs));
          const fz = oz * (1 - b / 4) + (hash2(b, 65, cs) - 0.5) * this.cellR * 0.7;
          const w = this.cellR * (0.3 + 0.25 * hash2(b, 66, cs));
          this.dummy.position.set(fx, this.baseY / 2, fz);
          this.dummy.scale.set(w, this.baseY, w * 0.8);
          this.dummy.rotation.set(0, hash2(b, 67, cs) * Math.PI, 0);
          this.dummy.updateMatrix();
          this.veil.setMatrixAt(v++, this.dummy.matrix);
        }
      }
      // In-cloud glow hangs at the cell's anchor (same point the block
      // illumination falls off from).
      this.glows[i].position.set(gp[i][0], gp[i][1], gp[i][2]);
    }
    this.veil.count = v;
    this.veil.instanceMatrix.needsUpdate = true;
  }

  set(name) {
    const want = name === "storm";
    if (want && !this.built) this.build();
    this.active = want;
    // The fair-weather decks (buildClouds' pastel puffs) dim under a storm —
    // bright mother-of-pearl cumulus over a black anvil line reads as two
    // skies arguing. main.js hands us the decks; restored when the round
    // clears. (Idempotent: set() runs on every snapshot.)
    if (this.cloudDecks) {
      const { low, high } = this.cloudDecks;
      if (this._deckOp === undefined) this._deckOp = [low.material.opacity, high.material.opacity];
      low.material.opacity = want ? this._deckOp[0] * 0.5 : this._deckOp[0];
      high.material.opacity = want ? this._deckOp[1] * 0.45 : this._deckOp[1];
    }
    if (this.built) {
      this.group.visible = want;
      if (!want) {
        this.closeRain.material.opacity = 0;
        this.bolt.material.opacity = 0;
        for (const g of this.groundGlows) g.material.opacity = 0;
        this.strikes.fill(null);
        this.arcs.fill(null);
      }
    }
  }

  // --- the deterministic lightning schedule, factored so update(), tests,
  // and the screenshot director all read the same dice ---------------------
  strikeRolls(slot, i) {
    return hash2(slot, 400 + i, this.seed) < 0.5;
  }
  strikeFireT(slot, i) {
    return slot * this.strikeEvery + hash2(slot, 410 + i, this.seed) * (this.strikeEvery - 1);
  }
  strikeBright(slot, i, u) {
    const ph = hash2(slot, 430 + i, this.seed);
    return Math.exp(-2.5 * u) * (0.45 + 0.55 * Math.abs(Math.sin(u * (36 + 30 * ph))));
  }
  // nextStrikeAt scans ahead from server-time fromT and returns the next
  // ground strike at its brightest instant: {t, cell, bright}. The director
  // parks cameras with this; nobody has to sit in the rain waiting.
  nextStrikeAt(fromT, horizonSec = 1800) {
    for (let slot = Math.floor(fromT / this.strikeEvery); slot * this.strikeEvery < fromT + horizonSec; slot++) {
      for (let i = 0; i < this.cellN; i++) {
        if (!this.strikeRolls(slot, i)) continue;
        const tFire = this.strikeFireT(slot, i);
        if (tFire < fromT) continue;
        let bt = tFire, bb = 0;
        for (let u = 0; u < 1; u += 0.02) {
          const b = this.strikeBright(slot, i, u);
          if (b > bb) { bb = b; bt = tFire + u * 0.65; }
        }
        return { t: bt, cell: i, bright: bb };
      }
    }
    return null;
  }

  // The front's pose at server-time t: crossing index, world position of the
  // line's center, and the march bearing (front-local +Z in world space).
  poseAt(t) {
    const s = t * this.speed;
    const k = Math.floor(s / this.pathLen);
    const along = s - k * this.pathLen - this.pathLen / 2;
    const ang = hash2(k, 7, this.seed) * Math.PI * 2;
    const dx = Math.sin(ang), dz = Math.cos(ang);
    // Each crossing also slides the whole track sideways so the front
    // doesn't always march through the map's center.
    const lat = (hash2(k, 8, this.seed) - 0.5) * this.field.radius * 0.9;
    return { k, x: dx * along - dz * lat, z: dz * along + dx * lat, ang };
  }

  update(dt, svrT, camera, sky) {
    if (!this.active || !this.built) {
      if (sky) sky.lightning = 0;
      this.insideK = 0;
      return;
    }
    const N = this.cellN;
    const pose = this.poseAt(svrT);
    if (pose.k !== this.crossK) this.layoutCells(pose.k);
    this.group.position.set(pose.x, 0, pose.z);
    this.group.rotation.y = pose.ang;
    const sinA = Math.sin(pose.ang), cosA = Math.cos(pose.ang);

    // World-space cell centers (the group only yaws + translates, so this is
    // a 2D rotation — no matrix math needed).
    this.cellWorld.length = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      this.cellWorld.push([pose.x + ox * cosA + oz * sinA, pose.z - ox * sinA + oz * cosA]);
    }

    // --- rain streaks: recycle down the shaft on the shared clock ----------
    const FALL = 62;
    const span = this.baseY + 60;
    let n = 0;
    for (let i = 0; i < N; i++) {
      const [ox, oz] = this.cellOff[i];
      const cs = (this.seed + i * 517) >>> 0;
      for (let b = 0; b < this.RAIN_PER_CELL; b++) {
        const ang = hash2(b, 71, cs) * Math.PI * 2;
        const rad = this.cellR * 0.8 * Math.sqrt(hash2(b, 72, cs));
        const phase = hash2(b, 73, cs) * span;
        const y = this.baseY + 20 - ((svrT * FALL + phase) % span);
        this.dummy.position.set(ox + Math.cos(ang) * rad, y, oz + Math.sin(ang) * rad);
        this.dummy.scale.set(2.2, 26 + hash2(b, 74, cs) * 18, 2.2);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.rain.setMatrixAt(n++, this.dummy.matrix);
      }
    }
    this.rain.instanceMatrix.needsUpdate = true;

    // --- how deep in the weather is the camera? ----------------------------
    // Horizontal closeness to the nearest cell, scaled by altitude: inside
    // the cloud band you get the full whiteout; below the base you're only
    // in the rain (a softer squeeze — hull crews on the sea/steppe keep
    // enough visibility to fight, and server spotting is untouched).
    let inK = 0;
    let nearCell = 0;
    for (let i = 0; i < N; i++) {
      const [wx, wz] = this.cellWorld[i];
      const d = Math.hypot(camera.position.x - wx, camera.position.z - wz);
      const k = Math.max(0, 1 - d / this.cellR);
      if (k > inK) { inK = k; nearCell = i; }
    }
    const camY = camera.position.y;
    const altK = camY > this.baseY * 0.85
      ? (camY < this.topY + 200 ? 1 : Math.max(0, 1 - (camY - this.topY - 200) / 400))
      : 0.3;
    const insideK = Math.min(1, inK * 2.2) * altK;
    this.insideK = insideK;
    if (insideK > 0.01) {
      // Deepen the fog the sky just set — knife-fight range inside a cell.
      this.scene.fog.near *= 1 - 0.72 * insideK;
      this.scene.fog.far *= 1 - 0.5 * insideK;
    }

    // --- close rain follows the camera while under/inside the weather ------
    const wet = inK > 0.05 && camY < this.topY ? Math.min(1, inK * 2.5) : 0;
    if (wet > 0.01) {
      const cs = (this.seed + 909) >>> 0;
      for (let b = 0; b < this.CLOSE_RAIN; b++) {
        const bx = (hash2(b, 81, cs) - 0.5) * 70;
        const bz = (hash2(b, 82, cs) - 0.5) * 70;
        const phase = hash2(b, 83, cs) * 60;
        const y = 30 - ((svrT * FALL + phase) % 60);
        this.dummy.position.set(camera.position.x + bx, camera.position.y + y, camera.position.z + bz);
        this.dummy.scale.set(1, 1, 1);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.closeRain.setMatrixAt(b, this.dummy.matrix);
      }
      this.closeRain.instanceMatrix.needsUpdate = true;
    }
    this.closeRain.material.opacity = wet * 0.55;

    // --- lightning ----------------------------------------------------------
    // Everything below is scheduled from hashed time slots, so every client
    // sees the same flicker at the same instant — a shared "did you see
    // that?!" moment, like the whales.
    let flash = 0; // feeds sky.lightning: the scene-wide cold pulse
    const cg = this.cellGlow;
    cg.fill(0);
    for (let i = 0; i < N; i++) {
      // In-cloud flicker: most slots glow somewhere in the belly — the
      // storm muttering to itself between the real strokes.
      const fSlot = Math.floor(svrT / this.flickerEvery);
      if (hash2(fSlot, 300 + i, this.seed) < 0.65) {
        const tFire = fSlot * this.flickerEvery + hash2(fSlot, 310 + i, this.seed) * (this.flickerEvery - 0.6);
        const u = (svrT - tFire) / 0.55;
        if (u >= 0 && u < 1) {
          const ph = hash2(fSlot, 320 + i, this.seed);
          cg[i] = Math.exp(-3 * u) * (0.35 + 0.65 * Math.abs(Math.sin(u * (22 + 14 * ph) + ph * 7)));
        }
      }

      // Ground strikes: rarer, harder, and they touch the actual terrain.
      let striking = false;
      const sSlot = Math.floor(svrT / this.strikeEvery);
      if (this.strikeRolls(sSlot, i)) {
        const tFire = this.strikeFireT(sSlot, i);
        // 0.65 s of life: a first hard stroke, then the restrikes real
        // lightning does — long enough for a human (or a screenshot) to
        // actually catch it.
        const u = (svrT - tFire) / 0.65;
        if (u >= 0 && u < 1) {
          striking = true;
          if (this.lastStrikeSlot[i] !== sSlot) {
            this.lastStrikeSlot[i] = sSlot;
            this.buildStrike(i, sSlot);
            if (this.onStrike && this.strikes[i]) this.onStrike(this.strikes[i].hit);
          }
          const st = this.strikes[i];
          if (st) {
            const b = this.strikeBright(sSlot, i, u);
            st.bright = b;
            this.groundGlows[i].position.copy(st.hit).add(_up12);
            this.groundGlows[i].material.opacity = b * 0.85;
            cg[i] = Math.max(cg[i], b);
            flash = Math.max(flash, b * 0.8);
          }
        }
      }
      if (!striking && this.strikes[i]) {
        this.strikes[i] = null;
        this.groundGlows[i].material.opacity = 0;
      }
    }

    // Cloud-to-cloud strokes: a stair-stepped arc crawling the bellies
    // between neighbor cells. It never touches the ground — its whole job
    // is lighting two cells from within at once.
    const c2cEvery = this.flickerEvery + 2.5;
    for (let j = 0; j + 1 < N; j++) {
      let arcing = false;
      const aSlot = Math.floor(svrT / c2cEvery);
      if (hash2(aSlot, 470 + j, this.seed) < 0.5) {
        const tFire = aSlot * c2cEvery + hash2(aSlot, 480 + j, this.seed) * (c2cEvery - 0.6);
        const u = (svrT - tFire) / 0.4;
        if (u >= 0 && u < 1) {
          arcing = true;
          if (this.lastArcSlot[j] !== aSlot) {
            this.lastArcSlot[j] = aSlot;
            this.buildArc(j, aSlot);
            if (this.onArc && this.arcs[j]) this.onArc(this.arcs[j].mid);
          }
          const arc = this.arcs[j];
          if (arc) {
            const ph = hash2(aSlot, 490 + j, this.seed);
            const b = Math.exp(-3 * u) * (0.4 + 0.6 * Math.abs(Math.sin(u * (28 + 22 * ph))));
            arc.bright = b;
            cg[j] = Math.max(cg[j], b * 0.9);
            cg[j + 1] = Math.max(cg[j + 1], b * 0.9);
            flash = Math.max(flash, b * 0.45);
          }
        }
      }
      if (!arcing && this.arcs[j]) this.arcs[j] = null;
    }

    // THE CLOUDS GLOW: blocks near a live stroke lerp toward a hot
    // blue-white, scaled by their baked falloff from the cell's glow
    // anchor. This is per-instance color, not a light — cheap, and it
    // reads from any distance. The >1 tint values push the nearest blocks
    // past their base brightness into a real flare.
    let lit = false;
    for (let i = 0; i < N; i++) if (cg[i] > 0.02) lit = true;
    if (lit || this._wasLit) {
      for (let m = 0; m < this.instCount; m++) {
        const b = Math.min(1, cg[this.instCell[m]] * this.instK[m] * 2.6);
        const o = m * 3;
        _c.setRGB(
          this.instBase[o] + (1.5 - this.instBase[o]) * b,
          this.instBase[o + 1] + (1.6 - this.instBase[o + 1]) * b,
          this.instBase[o + 2] + (2.0 - this.instBase[o + 2]) * b,
        );
        this.towers.setColorAt(m, _c);
      }
      this.towers.instanceColor.needsUpdate = true;
    }
    this._wasLit = lit;
    for (let i = 0; i < N; i++) {
      const g = Math.min(1, cg[i]);
      this.glows[i].material.opacity = g * 0.9;
      flash = Math.max(flash, g * 0.3);
    }
    if (sky) sky.lightning = flash;
    this.drawStrikes();
  }

  // Lay a stair-stepped bolt from cell i's belly to the terrain below —
  // axis-aligned boxes only: hard vertical drops and right-angle jogs, so
  // the sky's own violence speaks the same pixel language as the terrain it
  // hits. World anchored (the front drifts ~5 m during a strike's life; the
  // scorch point staying put is what sells "it hit the ground").
  buildStrike(i, slot) {
    const [wx, wz] = this.cellWorld[i];
    const cs = (this.seed + slot * 2711 + i * 389) >>> 0;
    // Bolts bias toward the LEADING edge of the march (+Z in the front's
    // frame): half-buried flashes inside the wall are mood, but the readable
    // crack-to-the-ground moments belong on the side the storm is walking
    // toward — the side everyone under it is watching.
    const ry = this.group.rotation.y;
    const lx = (hash2(0, 91, cs) - 0.5) * this.cellR * 1.2;
    const lz = this.cellR * (0.3 + 0.6 * hash2(0, 92, cs));
    const bx = wx + lx * Math.cos(ry) + lz * Math.sin(ry);
    const bz = wz - lx * Math.sin(ry) + lz * Math.cos(ry);
    const groundY = Math.max(this.field.surfaceAt(bx, bz), this.field.waterLevel ?? 0);
    const segs = [];
    const TH = 5;
    let x = bx, z = bz, y = this.baseY + 150;
    // The channel walks down in many small, uneven moves: mostly short
    // vertical steps, the odd long straight plunge, and every few steps a
    // SLANTED run (a leaned box) so the profile isn't all right angles —
    // jagged the way a pixel-art bolt is jagged, not a staircase diagram.
    for (let k = 0; y > groundY + 4 && segs.length < this.C2G_SEGS - 8; k++) {
      const r1 = hash2(k, 95, cs), r2 = hash2(k, 96, cs);
      const r3 = hash2(k, 97, cs), r4 = hash2(k, 88, cs);
      const drop = Math.min(y - groundY, r1 < 0.2 ? 90 + r2 * 130 : 16 + r2 * 55);
      if (r3 < 0.4 && drop > 24) {
        // Slanted run: lean 15°-50° off vertical, in X or Z.
        const lean = (0.26 + 0.62 * hash2(k, 87, cs)) * (r4 < 0.5 ? 1 : -1);
        const L = drop / Math.cos(lean);
        if (hash2(k, 86, cs) < 0.5) {
          const dx = -Math.sin(lean) * L;
          segs.push([x + dx / 2, y - drop / 2, z, TH, L + TH, TH, 0, lean]);
          x += dx;
        } else {
          const dz = Math.sin(lean) * L;
          segs.push([x, y - drop / 2, z + dz / 2, TH, L + TH, TH, lean, 0]);
          z += dz;
        }
        y -= drop;
      } else {
        segs.push([x, y - drop / 2, z, TH, drop + TH, TH]);
        y -= drop;
      }
      if (y <= groundY + 4) break;
      // Sometimes two drops run straight through; otherwise a hard
      // right-angle jog — narrow shrugs and wide throws both — with a pull
      // back toward the channel so the bolt wanders without walking away.
      if (hash2(k, 85, cs) < 0.25) continue;
      let len = (hash2(k, 84, cs) - 0.5) * 2 * (hash2(k, 83, cs) < 0.5 ? 34 : 130);
      if (r3 >= 0.5) {
        len -= (x - bx) * 0.5;
        segs.push([x + len / 2, y, z, Math.abs(len) + TH, TH, TH]);
        x += len;
      } else {
        len -= (z - bz) * 0.5;
        segs.push([x, y, z + len / 2, TH, TH, Math.abs(len) + TH]);
        z += len;
      }
    }
    // One forked branch off the upper channel, dying out mid-air — the
    // detail every pixel-lightning drawing agrees on.
    let fx = bx, fz = bz;
    let fy = this.baseY + 150 - (60 + hash2(1, 98, cs) * 120);
    const fTH = 3.2;
    const fSign = hash2(0, 93, cs) < 0.5 ? 1 : -1;
    for (let f = 0; f < 4 && segs.length < this.C2G_SEGS; f++) {
      if (f % 2 === 0) {
        const len = (18 + hash2(f, 99, cs) * 70) * fSign;
        segs.push([fx + len / 2, fy, fz, Math.abs(len) + fTH, fTH, fTH]);
        fx += len;
      } else {
        const drop = 40 + hash2(f, 94, cs) * 80;
        segs.push([fx, fy - drop / 2, fz, fTH, drop + fTH, fTH]);
        fy -= drop;
      }
    }
    this.strikes[i] = { segs, hit: new THREE.Vector3(x, groundY, z), bright: 0 };
  }

  // A cloud-to-cloud stroke: a stair-stepped arc crawling the bellies from
  // cell j toward cell j+1 — long horizontal bars with vertical kicks,
  // glimpsed through the cloud mass it lights up.
  buildArc(j, slot) {
    const [ax, az] = this.cellWorld[j];
    const [bx, bz] = this.cellWorld[j + 1];
    const cs = (this.seed + slot * 3499 + j * 577) >>> 0;
    const band = this.topY - this.baseY;
    let y = this.baseY + band * (0.22 + 0.25 * hash2(0, 81, cs));
    let x = ax + (hash2(0, 82, cs) - 0.5) * this.cellR * 0.5;
    let z = az + (hash2(0, 83, cs) - 0.5) * this.cellR * 0.5;
    const tx = bx + (hash2(0, 84, cs) - 0.5) * this.cellR * 0.5;
    const tz = bz + (hash2(0, 85, cs) - 0.5) * this.cellR * 0.5;
    const segs = [];
    const TH = 5.0; // thick enough to read through a kilometre of cloud
    const STEPS = 5;
    for (let sIdx = 0; sIdx < STEPS && segs.length < this.C2C_SEGS - 2; sIdx++) {
      const legX = (tx - x) / (STEPS - sIdx);
      const legZ = (tz - z) / (STEPS - sIdx);
      segs.push([x + legX / 2, y, z, Math.abs(legX) + TH, TH, TH]);
      x += legX;
      segs.push([x, y, z + legZ / 2, TH, TH, Math.abs(legZ) + TH]);
      z += legZ;
      const kick = (hash2(sIdx, 86, cs) - 0.5) * band * 0.3;
      segs.push([x, y + kick / 2, z, TH, Math.abs(kick) + TH, TH]);
      y += kick;
    }
    this.arcs[j] = {
      segs,
      mid: new THREE.Vector3((ax + bx) / 2, y, (az + bz) / 2),
      bright: 0,
    };
  }

  drawStrikes() {
    let n = 0;
    let anyBright = 0;
    const put = (st) => {
      if (!st || st.bright <= 0.01) return;
      anyBright = Math.max(anyBright, st.bright);
      for (const [x, y, z, sx, sy, sz, rx, rz] of st.segs) {
        if (n >= this.bolt.count) return;
        this.dummy.position.set(x, y, z);
        this.dummy.scale.set(sx, sy, sz);
        this.dummy.rotation.set(rx ?? 0, 0, rz ?? 0);
        this.dummy.updateMatrix();
        this.bolt.setMatrixAt(n++, this.dummy.matrix);
      }
    };
    for (const st of this.strikes) put(st);
    for (const arc of this.arcs) put(arc);
    // Park unused segments at zero scale rather than juggling mesh.count.
    this.dummy.scale.set(0, 0, 0);
    this.dummy.updateMatrix();
    for (let j = n; j < this.bolt.count; j++) this.bolt.setMatrixAt(j, this.dummy.matrix);
    this.bolt.instanceMatrix.needsUpdate = true;
    this.bolt.material.opacity = anyBright;
  }
}

const _c = new THREE.Color();
const _up12 = new THREE.Vector3(0, 14, 0);
